import React from 'react'
import CommonGrid from '@mailstep/design-system/ui/Blocks/CommonGrid'
import GridExport, { Props as GridExportProps } from '@components/blocks/GridExport'
import { GridProps } from '@mailstep/design-system/ui/Blocks/CommonGrid/types'
import { useModal } from '@mailstep/design-system/ui/Blocks/Modal/hooks/useModal'
import useShallowRouting from './useShallowRouting'
import { x } from '@xstyled/styled-components'
import { t } from '@lingui/macro'

type StyledCommonGridProps = {
  onExport?: GridExportProps['onExport']
  headersOnlyOption?: boolean
}

export type Props = GridProps & StyledCommonGridProps

export type PageGridProps = Omit<
  GridProps,
  'rowsData' | 'totalRowsCount' | 'columnsDefinitions' | 'actionColumnDefinition' | 'minColumnWidth' | 'columnLayout'
> &
  StyledCommonGridProps

const comparators = () => ({
  text: [
    { label: t({ id: 'dataGrid.comparator.contains', message: 'Contains' }), value: 'like', icon: 'check' },
    { label: t({ id: 'dataGrid.comparator.notContains', message: 'Not contains' }), value: 'notLike', icon: 'false' },
    { label: t({ id: 'dataGrid.comparator.equals', message: 'Equals' }), value: 'eq', icon: 'equals' },
    { label: t({ id: 'dataGrid.comparator.notEquals', message: 'Not equal' }), value: 'neq', icon: 'notEqual' },
    { label: t({ id: 'dataGrid.comparator.startsWith', message: 'Starts with' }), value: 'startsWith', icon: 'startsWith' },
    { label: t({ id: 'dataGrid.comparator.endsWith', message: 'Ends with' }), value: 'endsWith', icon: 'endsWith' },
    { label: t({ id: 'dataGrid.comparator.isEmpty', message: 'Is empty' }), value: 'isEmpty', icon: 'notEqual' },
  ],
})

const StyledCommonGrid = ({ onExport, headersOnlyOption, gridSelectors, gridActions, ...props }: Props): JSX.Element => {
  const { onOpen: onExportClick, isOpen: exportFormVisible, onClose: hideExport } = useModal()

  const { changePathOrSetPage, rowsPerPageValue } = useShallowRouting(gridActions, gridSelectors)

  return (
    <x.div h="calc(100% - 65px)">
      <CommonGrid
        processCheckedValues={onExportClick}
        gridActions={gridActions}
        gridSelectors={gridSelectors}
        queryRowsParam={rowsPerPageValue}
        customPaginationHandler={changePathOrSetPage}
        comparators={comparators()}
        {...props}
      />
      {!!onExport && exportFormVisible && (
        <GridExport
          onClose={hideExport}
          onExport={onExport}
          gridSelectors={gridSelectors}
          headersOnlyOption={headersOnlyOption}
        />
      )}
    </x.div>
  )
}

export default StyledCommonGrid
